import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { StorageConstant } from '@shared/constants/storage.constant';
import { LocalStorageUtil } from '@shared/utils/local-storage.util';
import { UserPreferencesService } from '@shared-features/services/user-preferences.service';
import { UserPreferencesType } from '@shared-features/enums/user-preferences-type.enum';
import { map, tap } from 'rxjs/operators';
import { ColumnOptionsState } from '@shared-features/models/column-options-state.model';

@Injectable({
  providedIn: 'root',
})
export class ColumnOptionsService {
  state$ = new Subject<ColumnOptionsState>();
  drawer$ = new Subject();
  currentStateId: string;

  private storage: {
    [k: string]: ColumnOptionsState;
  } = {};

  /**
   * Opt-in server-side persistence, registered per list by the list itself.
   *
   * Registration IS the opt-in, and that is the whole safety mechanism: a list with no persister
   * keeps the existing user-preferences behaviour untouched, and nothing here can redirect it.
   *
   * A callback rather than an endpoint of its own, because each list's record shape is its own
   * business — and it keeps the dependency direction right: the feature reaches into `shared/`,
   * never the reverse.
   */
  private readonly persisters = new Map<string, () => Observable<unknown>>();

  constructor(private userPreferencesService: UserPreferencesService) {
    this.storage = LocalStorageUtil.getItem(StorageConstant.COLUMN_OPTIONS_KEY) || {};
    this.getSavedStorage();
  }

  set state({
    id,
    keys,
    state,
    preferences: newPreferences,
    lockedKeys,
    resetState,
    hideDynamicFieldsToggle,
    hideCustomColumnSettings,
    rawLabels,
  }: Partial<ColumnOptionsState>) {
    const currentColOptions = this.storage[id];
    const oldState = currentColOptions?.defaultState || state || [];
    const newState = state && state.length ? state : oldState;

    const oldKeys = currentColOptions?.keys;
    const newKeys = keys ? keys : oldKeys;
    const preferences = newPreferences || currentColOptions?.preferences || [];

    // The three below are absent for every pre-existing list, so they stay undefined there.
    const newLockedKeys = lockedKeys ?? currentColOptions?.lockedKeys;
    const newResetState = resetState ?? currentColOptions?.resetState;
    const newHideToggle = hideDynamicFieldsToggle ?? currentColOptions?.hideDynamicFieldsToggle;
    const newHideCustom = hideCustomColumnSettings ?? currentColOptions?.hideCustomColumnSettings;
    const newRawLabels = rawLabels ?? currentColOptions?.rawLabels;

    this.storage = {
      ...this.storage,
      [id]: {
        id,
        keys: newKeys,
        state: newState,
        defaultState: oldState,
        preferences,
        lockedKeys: newLockedKeys,
        resetState: newResetState,
        hideDynamicFieldsToggle: newHideToggle,
        hideCustomColumnSettings: newHideCustom,
        rawLabels: newRawLabels,
      } as ColumnOptionsState,
    };

    LocalStorageUtil.setItem(StorageConstant.COLUMN_OPTIONS_KEY, this.storage);
    this.currentStateId = id;
    this.state$.next(this.storage[id]);
  }

  /** Opts a list into its own persistence. Call from the owning feature's init. */
  registerPersister(id: string, persist: () => Observable<unknown>): void {
    this.persisters.set(id, persist);
  }

  /** Drops the registration, so a destroyed feature service is neither retained nor called. */
  unregisterPersister(id: string): void {
    this.persisters.delete(id);
  }

  getStateByName(id: string): ColumnOptionsState {
    return this.storage[id];
  }

  toggleDrawer(shown?: boolean): void {
    this.drawer$.next(shown);
  }

  getSavedStorage(): void {
    this.userPreferencesService
      .getUserPreferences(UserPreferencesType.Columns)
      .pipe(
        map((res) =>
          this.userPreferencesService.mapJsonToModel<ColumnOptionsState[]>(ColumnOptionsState, res)
        )
      )
      .subscribe((storage) => {
        this.storage = Object.assign(
          this.storage,
          (storage || []).reduce((group, item) => {
            group[item.id] = item;
            return group;
          }, {})
        );
        LocalStorageUtil.setItem(StorageConstant.COLUMN_OPTIONS_KEY, this.storage);
        if (this.currentStateId) {
          this.state$.next(this.storage[this.currentStateId]);
        }
      });
  }

  /**
   * Persists a list's arrangement.
   *
   * With an `id` that has a registered persister, ONLY that persister runs — the list owns its own
   * endpoint and must not be uploaded into the shared user-preferences record. Every other call,
   * including all four pre-existing call sites that pass no argument, takes the unchanged
   * user-preferences path below.
   */
  saveStorage(id?: string): Observable<any> {
    const persist = id ? this.persisters.get(id) : undefined;
    if (persist) {
      // The failure is NOT swallowed here. Returning `of(null)` on error made a rejected save look
      // like a successful one to the caller, which then showed the success toast.
      return persist();
    }

    const storage = Object.values(this.storage);
    return this.userPreferencesService
      .updateUserPreferences(storage, UserPreferencesType.Columns)
      .pipe(tap(() => this.getSavedStorage()));
  }
}
